import { MouseEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Link, List, Paragraph } from '@digdir/designsystemet-react';
import { useUserOnboardingQuery } from '../../../../hooks/queries/onboarding/useUserOnboarding';
import { useRiskMatrixSetupQuery } from '../../../../hooks/queries/hms/useHmsQueries';
import { Routes } from '../../../../enums/Routes';
import { FROM_SURVEY_PARAM, FROM_SURVEY_VALUE } from '../../../CreateSurveyPage/RiskMatrixNotice';
import { homeFor, isOpen } from '../../../GettingStarted/gettingStartedView';
import { FIELD_IDS } from '../riskMatrixView';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskMatrix.module.css';

const K = 'hrRiskMatrix.guide';

/**
 * «Kom i gang» sent HR here to set up the risk matrix: the three settings that matter most before the first
 * mapping. Once a version is saved, the step is done, and HR is shown the way back. HR who never changed the
 * standard matrix is shown the same, whatever the goal, and the way back to the survey when they came from it.
 */
export const GettingStartedGuide = (): ReactElement | null => {
  const { t } = useTranslation();
  const { data: onboarding } = useUserOnboardingQuery();
  // The page is only for HR, so the setup is HR's to do
  const { data: setup } = useRiskMatrixSetupQuery();
  const [params] = useSearchParams();
  const fromSurvey = params.get(FROM_SURVEY_PARAM) === FROM_SURVEY_VALUE;
  const active = Boolean(onboarding?.gettingStarted && onboarding.goal === 'WORK_ENVIRONMENT');
  const stepOpen = Boolean(active && onboarding && isOpen(onboarding, 'riskMatrix'));
  const open = stepOpen || setup?.changed === false;
  // The guide was shown on this visit, and why, so saving tells HR where to go next
  const [guided, setGuided] = useState<'gettingStarted' | 'setup' | null>(null);
  if (stepOpen && guided !== 'gettingStarted') setGuided('gettingStarted');
  else if (open && !guided) setGuided('setup');
  const saved = useRef<HTMLDivElement>(null);
  const done = guided && !open;
  // The form is saved at the bottom of the page, so the way back is brought into view
  useEffect(() => {
    if (done) saved.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [done]);

  if (!open) {
    if (!guided) return null;
    // Only those with «Kom i gang» are sent back to it
    const home = guided === 'gettingStarted' ? homeFor(onboarding?.goal ?? null) : null;
    const back = fromSurvey
      ? { to: Routes.CreateSurvey, label: t(`${K}.backToSurvey`) }
      : home
        ? { to: home, label: t(`${K}.back`) }
        : null;
    return (
      <Alert ref={saved} data-color="success" role="status">
        <Paragraph>{t(`${K}.done`)}</Paragraph>
        {back && (
          <Link asChild>
            <RouterLink to={back.to}>{back.label}</RouterLink>
          </Link>
        )}
      </Alert>
    );
  }

  // The top bar would cover a field scrolled to the top, so it is brought to the middle and focused
  const goTo = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    const field = document.getElementById(href.slice(1));
    if (!field) return;
    event.preventDefault();
    field.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    field.focus({ preventScroll: true });
  };
  const settings = [
    { key: 'links', href: `#${FIELD_IDS.whistleblowingUrl}` },
    { key: 'skip', href: `#${FIELD_IDS.sensitiveSkip}` },
    { key: 'minResponses', href: `#${FIELD_IDS.minResponses}` },
  ];
  return (
    <section aria-labelledby="hr-rm-guide" className={layout.card} data-gap="sm" data-highlight="true">
      <div className={layout.cardHeading}>
        <span className={layout.step}>{guided === 'gettingStarted' ? t(`${K}.eyebrow`) : t(`${K}.eyebrowSetup`)}</span>
        <Heading level={2} data-size="sm" id="hr-rm-guide">
          {t(`${K}.title`)}
        </Heading>
        <Paragraph data-size="sm" className={layout.cardIntro}>
          {t(`${K}.intro`)}
        </Paragraph>
      </div>
      <List.Ordered data-size="sm" className={classes.guideList}>
        {settings.map(({ key, href }) => (
          <List.Item key={key}>
            <a href={href} className="ds-link" onClick={(event) => goTo(event, href)}>
              {t(`${K}.${key}.title`)}
            </a>
            <br />
            <span className={layout.subtle}>{t(`${K}.${key}.text`)}</span>
          </List.Item>
        ))}
      </List.Ordered>
      <Paragraph data-size="sm">{t(`${K}.save`)}</Paragraph>
    </section>
  );
};
